/**
 * Reading rhythm hook for the Adaptive Cinematic Reveal Engine.
 *
 * Splits a message into the right cadence for the chosen style:
 *   - 'word'      → word-by-word (intimate / ethereal)
 *   - 'sentence'  → sentence-by-sentence with breath pauses (film / editorial)
 *   - 'paragraph' → paragraph fade (futuristic)
 *
 * Auto-detects emphasis chunks (first sentence, last sentence, lines that
 * begin with "I ", "You ", "Thank") so they get a small extra pause + glow.
 *
 * Total reveal duration targets 8–18 s based on message length and pacing.
 */

import { useMemo } from 'react';
import type { RevealStylePreset } from '../adaptive/revealStyles';

export type RhythmMode = RevealStylePreset['readingRhythm'];

export interface ReadingChunk {
  text: string;
  /** Cumulative delay (s) before this chunk appears */
  delay: number;
  /** Per-chunk fade duration (s) */
  duration: number;
  /** Whether to give it the emphasis treatment */
  emphasis: boolean;
  /** Whether to insert a small visual breath after this chunk */
  breath: boolean;
}

export interface ReadingRhythm {
  mode: RhythmMode;
  chunks: ReadingChunk[];
  /** Total time (s) the body needs to fully reveal */
  totalDuration: number;
}

interface UseReadingRhythmInput {
  message: string;
  mode: RhythmMode;
  /** Pacing multiplier from adaptiveConfig.pacing */
  pacing: number;
  /** Initial delay before first chunk (s) */
  startDelay?: number;
}

const EMPHASIS_PREFIXES = [
  'i ', "i'", 'i,', 'you ', 'you,', "you'", 'thank', 'forever', 'always', 'never', 'remember',
];

function isEmphasis(text: string, idx: number, total: number): boolean {
  if (idx === 0 || idx === total - 1) return true;
  const lower = text.trim().toLowerCase();
  return EMPHASIS_PREFIXES.some((p) => lower.startsWith(p));
}

function splitSentences(message: string): string[] {
  // Keep punctuation; split on . ! ? followed by whitespace or end.
  const parts = message
    .replace(/\s+/g, ' ')
    .trim()
    .split(/(?<=[.!?])\s+/g)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : [message.trim()];
}

function splitParagraphs(message: string): string[] {
  const parts = message.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return parts.length > 0 ? parts : [message.trim()];
}

export function computeReadingRhythm({
  message,
  mode,
  pacing,
  startDelay = 0,
}: UseReadingRhythmInput): ReadingRhythm {
  const targetMin = 8; // seconds
  const targetMax = 18; // seconds

  if (mode === 'word') {
    const words = message.split(/(\s+)/).filter((w) => w.trim().length > 0);
    // Per-word delay: tuned for 8–14 s on a typical 200–500 char message.
    const baseStep = Math.min(0.18, Math.max(0.07, (targetMin / Math.max(words.length, 12))));
    const step = baseStep * pacing;
    let cursor = startDelay;
    const chunks: ReadingChunk[] = words.map((w, i) => {
      const emphasis = isEmphasis(w, i, words.length);
      const breath = /[.!?,]$/.test(w);
      const delay = cursor;
      cursor += step + (breath ? step * 0.6 : 0) + (emphasis ? step * 0.3 : 0);
      return {
        text: w,
        delay,
        duration: 0.55 * pacing,
        emphasis,
        breath,
      };
    });
    const totalDuration = Math.min(targetMax + 4, Math.max(targetMin, cursor + 0.5));
    return { mode, chunks, totalDuration };
  }

  if (mode === 'sentence') {
    const sentences = splitSentences(message);
    // Each sentence appears with a measured beat; length affects breath.
    let cursor = startDelay;
    const chunks: ReadingChunk[] = sentences.map((s, i) => {
      const emphasis = isEmphasis(s, i, sentences.length);
      const beat = Math.min(2.4, Math.max(1.0, 0.9 + s.length / 120)) * pacing;
      const delay = cursor;
      cursor += beat + (emphasis ? 0.5 * pacing : 0.25 * pacing);
      return {
        text: s,
        delay,
        duration: 1.1 * pacing,
        emphasis,
        breath: true,
      };
    });
    const totalDuration = Math.min(targetMax + 6, Math.max(targetMin, cursor + 0.6));
    return { mode, chunks, totalDuration };
  }

  // paragraph
  const paragraphs = splitParagraphs(message);
  let cursor = startDelay;
  const chunks: ReadingChunk[] = paragraphs.map((p, i) => {
    const emphasis = i === 0 || i === paragraphs.length - 1;
    const beat = Math.min(3.4, Math.max(1.6, 1.4 + p.length / 220)) * pacing;
    const delay = cursor;
    cursor += beat;
    return {
      text: p,
      delay,
      duration: 1.4 * pacing,
      emphasis,
      breath: true,
    };
  });
  const totalDuration = Math.min(targetMax + 8, Math.max(targetMin, cursor + 0.8));
  return { mode, chunks, totalDuration };
}

export function useReadingRhythm({
  message,
  mode,
  pacing,
  startDelay = 0,
}: UseReadingRhythmInput): ReadingRhythm {
  return useMemo(
    () => computeReadingRhythm({ message, mode, pacing, startDelay }),
    [message, mode, pacing, startDelay],
  );
}
